import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import './styles/app.css';
import { createRoot } from 'react-dom/client';
import { NotFound, RouteError } from './components/layout/route-states';
import { ApiError } from './lib/api';
import { installClickFeedback } from './lib/click-feedback';
import { isPublicPath } from './lib/session';
import { applyTheme, readTheme } from './lib/theme';
import { routeTree } from './routeTree.gen';

// Requests are attached to the button click that started them (busy bar, then done or failed on the button).
installClickFeedback();

const queryClient = new QueryClient({
  // A session that ended (idle timeout, suspended, signed out elsewhere) sends the person to sign in, back here after.
  queryCache: new QueryCache({
    onError: error => {
      if (error instanceof ApiError && error.status === 401 && !isPublicPath(window.location.pathname))
        window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Requests the server refused (4xx) fail at once; network and server failures get two more tries.
      retry: (count, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429) && count < 2,
    },
  },
});
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

applyTheme(readTheme());
const root = document.getElementById('root');
if (!root) throw new Error('#root missing');
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
